import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { SalesRouteStop, VisitDraft } from './sales-route.models';

/**
 * Recording what happened at one door: the outcome buttons, the note, and the
 * voice report that fills both in.
 *
 * Its own class because it is the one part of this page that owns a BROWSER
 * DEVICE. A microphone has a lifecycle - permission, an open stream, a hard
 * stop, tracks that must be released whether the dialog was closed, saved or
 * navigated away from - and on the component that lifecycle was interleaved
 * with route loading, planning and the edit session, where it could only be
 * exercised through the whole page. Nothing here needs a view, a session or a
 * map, so a test can open the recorder, feed it a stream and assert what it
 * released.
 *
 * It reaches back into the page for exactly two things: an HttpClient, and
 * "the day changed, load it again". That is the whole contract, and it is
 * small on purpose - a recorder that needed to know about days, reps or plans
 * would not be a recorder.
 */
export class SalesRouteVisitRecorder {
  /** Server enforces this too; the client just stops before it gets there. */
  static readonly MaxReportSeconds = 90;

  /** Outcome vocabulary. Same list as the server and as the agent's response
   *  classification - one taxonomy for the same fact, not two. */
  static readonly Outcomes = [
    { value: 'Interested', label: 'Interested' },
    { value: 'NeedsQuote', label: 'Wants a quote' },
    { value: 'NotNow', label: 'Not now' },
    { value: 'WrongContact', label: 'Wrong contact' },
    { value: 'NotQualified', label: 'Not qualified' },
  ];

  /** Which stop is being recorded, and what has been said about it. */
  recording: SalesRouteStop | null = null;
  draftOutcome: string | null = null;
  draftNote = '';
  saving = false;
  /** Voice state. listening = the mic is open; reading = the agent is working. */
  listening = false;
  reading = false;
  error: string | null = null;
  draft: VisitDraft | null = null;

  private recorder?: MediaRecorder;
  private chunks: Blob[] = [];
  private stopTimer?: ReturnType<typeof setTimeout>;

  /**
   * The open microphone, held here rather than only in the `onstop` closure.
   *
   * Releasing it there covers the ordinary path and nothing else: `onstop`
   * only runs for a recorder that reached `recording`, so a stream opened by a
   * `getUserMedia` that resolved into a construction failure was never given
   * back. The tracks belong to this object for as long as they are open, so
   * this object is what holds them.
   */
  private stream: MediaStream | null = null;

  /**
   * Which attempt at recording is the current one.
   *
   * `getUserMedia` is a permission prompt, so the await can outlive the thing
   * that asked - the rep closes the dialog, or leaves the page entirely, while
   * the browser is still asking. The old code resumed regardless: it found no
   * recorder to stop on the way out, then opened a microphone afterwards and
   * left `listening` true, with nothing remaining that would ever close it.
   * That is a recording indicator the user cannot get rid of.
   *
   * Bumped by every discard, so an attempt that comes back late can see that
   * it is stale, hand the stream straight back, and touch nothing else.
   */
  private attempt = 0;

  /**
   * Requests this DIALOG started, cancelled whenever the dialog moves on.
   *
   * Every one of them writes into state that belongs to the door currently
   * open - the draft, or the flag that gates the control that started it - so
   * an answer that outlives its door is an answer applied to the wrong lead.
   * The transcription puts one rep's words into another's note. The save
   * closes a dialog the rep has since opened somewhere else, mid-typing. And
   * both `reading` and `saving` gate their own controls, so either one left
   * true by a dialog that closed refuses every later attempt for the life of
   * the page - silently, because both guards simply return.
   *
   * Replaced rather than reused after unsubscribing: adding to a closed
   * Subscription cancels the new work immediately.
   */
  private dialogWork = new Subscription();

  /**
   * Requests the LIST started, which outlive the dialog on purpose.
   *
   * Skipping a stop is initiated from the row, not from the dialog, so a rep
   * who skips one door and opens another must not have the skip called off by
   * the opening. Only teardown cancels these.
   */
  private pageWork = new Subscription();

  /**
   * @param http    the page's client, so one interceptor stack applies
   * @param reload  called after a write lands, because what changed is the day
   *                and the day belongs to the page
   */
  constructor(
    private readonly http: HttpClient,
    private readonly reload: () => void,
  ) {}

  followUpType: 'Call' | 'Email' | null = null;
  followUpDate = '';
  readonly outcomes = SalesRouteVisitRecorder.Outcomes;

  /** True while a door is open for recording. */
  get open(): boolean { return this.recording !== null; }

  /** True when the agent produced a memo but would not classify it. Said out
   *  loud, because a blank chip is otherwise read as a bug. */
  get draftHasNoOutcome(): boolean {
    return this.draft != null && !this.draft.outcome;
  }

  openFor(stop: SalesRouteStop): void {
    // A new door starts from a clean device and a clean upload. Reached without
    // closing the last one - the list opens a stop directly - so doing this only
    // in `close()` would leave the previous door's transcription to land in this
    // one's note.
    this.discardRecording();
    this.followUpType = null; this.followUpDate = '';
    this.recording = stop;
    this.draftOutcome = stop.outcome;
    this.draftNote = stop.note ?? '';
    this.draft = null;
    this.error = null;
  }

  close(): void {
    this.discardRecording();
    this.recording = null;
    this.draft = null;
    this.draftOutcome = null;
    this.draftNote = '';
    this.error = null;
  }

  /** The quick path: one tap, no microphone, no model. */
  save(): void {
    if (!this.recording || !this.draftOutcome || this.saving || (this.followUpType && !this.followUpDate)) return;
    this.saving = true;
    this.dialogWork.add(this.http.post<SalesRouteStop>(
      `/api/sales-routes/stops/${this.recording.id}/visit`,
      { outcome: this.draftOutcome, note: this.draftNote || null, contactId: null, nextActionType: this.followUpType, nextActionDue: this.followUpType ? this.followUpDate : null }).subscribe({
      next: () => { this.saving = false; this.close(); this.reload(); },
      error: err => {
        this.saving = false;
        this.error = err.status === 403
          ? 'You do not have permission to record visits.'
          : 'Could not save the visit.';
      },
    }));
  }

  skip(stop: SalesRouteStop, reason?: string): void {
    if (!reason?.trim() || this.saving) return;
    this.saving = true;
    this.pageWork.add(this.http.post<SalesRouteStop>(
      `/api/sales-routes/stops/${stop.id}/skip`, { reason: reason.trim() }).subscribe({
      next: () => { this.saving = false; this.reload(); },
      error: () => { this.saving = false; this.error = 'Could not skip the stop.'; },
    }));
  }

  /**
   * Voice report. Dictated AFTER leaving - this is a note to yourself, not a
   * recording of the conversation, which is why it is capped and why the button
   * lives inside the "record result" step rather than on the stop card.
   */
  async startVoice(): Promise<void> {
    if (this.listening || this.reading) return;
    this.error = null;
    const mine = ++this.attempt;
    let stream: MediaStream | undefined;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Anything that discarded while the prompt was up wins. Handing the
      // stream straight back is the whole point: there is no longer anybody
      // watching this recorder who would close it later.
      if (mine !== this.attempt) { releaseTracks(stream); return; }

      this.stream = stream;
      this.chunks = [];
      this.recorder = new MediaRecorder(stream);
      this.recorder.ondataavailable = e => { if (e.data.size) this.chunks.push(e.data); };

      // A recording can end without anybody asking. Two signals, because
      // neither is reliable alone: `onerror` is what the spec offers and what
      // browsers disagree about, and a track that ENDS is what actually
      // happens when the operating system hands the microphone to a phone
      // call or another application. Whichever arrives first, the state has to
      // stop claiming to be recording - otherwise `listening` stays true, the
      // button says "stop", and pressing it finds a recorder that is already
      // inactive.
      this.recorder.onerror = () => this.abandonRecording(
        'The recording stopped unexpectedly. Your note is still here - try again.');
      stream.getTracks().forEach(track => {
        track.onended = () => this.abandonRecording(
          'The microphone was taken by something else. Your note is still here - try again.');
      });
      this.recorder.onstop = () => {
        this.releaseStream();
        // A discard stops the recorder to close the device, not to send what it
        // captured. Checked rather than relying on `chunks` having been cleared
        // first, which is an ordering, not a decision.
        if (mine === this.attempt) this.sendVoice();
      };
      this.recorder.start();
      this.listening = true;
      // Hard stop. The ceiling is what keeps this a self-dictated note.
      this.stopTimer = setTimeout(
        () => this.stopVoice(), SalesRouteVisitRecorder.MaxReportSeconds * 1000);
    } catch {
      // The prompt may well have succeeded and the recorder failed after it -
      // an unsupported container, a device that went away between the two. The
      // stream is open either way, so it goes back either way.
      this.stream = stream ?? null;
      this.releaseStream();
      this.recorder = undefined;
      this.listening = false;
      this.error = 'No microphone available. Use the buttons above instead.';
    }
  }

  stopVoice(): void {
    clearTimeout(this.stopTimer);
    if (this.recorder?.state === 'recording') this.recorder.stop();
    else this.releaseStream();
    this.listening = false;
  }

  /** Give the microphone back. Safe when nothing is recording and safe twice -
   *  a page tears down on route change without knowing whether a mic is open. */
  discardRecording(): void {
    // First, so a prompt still on screen cannot come back and open a device
    // behind a dialog that has already gone.
    this.attempt++;
    clearTimeout(this.stopTimer);
    if (this.recorder?.state === 'recording') this.recorder.stop();
    // Unconditional. `onstop` only runs for a recorder that got as far as
    // recording; every other way of arriving here still has tracks to give up.
    this.releaseStream();
    this.recorder = undefined;
    this.chunks = [];
    this.listening = false;
    // Whatever this dialog had asked for goes with it. Clearing the two flags
    // is not cosmetic: each gates the control that set it, so one left true
    // refuses every later recording, or every later save.
    this.dialogWork.unsubscribe();
    this.dialogWork = new Subscription();
    this.reading = false;
    this.saving = false;
  }

  /**
   * Everything, including the requests the list started. What a page tears down
   * with - `discardRecording` is the dialog moving on, which is not the same
   * event and must not cancel a skip.
   */
  destroy(): void {
    this.discardRecording();
    this.pageWork.unsubscribe();
    this.pageWork = new Subscription();
  }

  /**
   * The device stopped without being asked. Give it back and say so.
   *
   * Deliberately NOT `discardRecording`, which is the dialog moving on: that
   * also cancels the requests this dialog started, and a microphone failing is
   * no reason to abandon a save the rep pressed a moment earlier. What ends
   * here is the recording, and nothing else.
   *
   * What was captured is dropped rather than sent. A container cut off partway
   * is not reliably readable, and offering a transcription that may be missing
   * its ending is worse than saying plainly that the recording stopped. The
   * typed note is left alone - it may be the only thing the rep still has.
   */
  private abandonRecording(message: string): void {
    if (!this.listening && !this.stream) return;   // already handled; both signals can fire
    // Stale, so an `onstop` arriving behind this cannot send the fragment.
    this.attempt++;
    clearTimeout(this.stopTimer);
    this.releaseStream();
    this.recorder = undefined;
    this.chunks = [];
    this.listening = false;
    this.error = message;
  }

  private releaseStream(): void {
    releaseTracks(this.stream);
    this.stream = null;
  }

  private sendVoice(): void {
    if (!this.recording || this.chunks.length === 0) return;
    this.reading = true;
    const form = new FormData();
    form.append('audio', new Blob(this.chunks, { type: 'audio/webm' }), 'report.webm');
    this.dialogWork.add(this.http.post<VisitDraft | { draft: null; reason: string }>(
      `/api/sales-routes/stops/${this.recording.id}/capture`, form).subscribe({
      next: res => {
        this.reading = false;
        if (!res || !('memo' in res)) {
          this.error = 'Voice reports are not configured here. Use the buttons above.';
          return;
        }
        // A DRAFT. Nothing is written until the rep presses Save.
        this.draft = res;
        this.draftNote = res.memo;
        if (res.outcome) this.draftOutcome = res.outcome;
      },
      error: err => {
        this.reading = false;
        this.error = err.error?.error
          ?? 'Could not read the report. Use the buttons above instead.';
      },
    }));
  }
}

/** Stop every track, or do nothing when there is no stream. Stopping a stopped
 *  track is a no-op per spec, so this is safe to call more than once. */
function releaseTracks(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach(track => track.stop());
}
